import { useState, useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useStores } from 'src/hooks/use-stores';
import { EDITABLE_HOMEPAGE_CAROUSEL_HEADINGS_FLAG } from 'shared/constants';
import { MenuSectionType, MobileAppProductCollectionDisplayType, MobileAppFlair } from 'types/graphql';
import { LocalSection, Products } from '../../homepage-form.types';
import { getMenuSectionProductFilter, getProductIds } from '../../homepage-form.utils';
import { getVisibleCategoryOptions, getVisibleSubcategoryOptions } from './add-category-modal.utils';
import { CategoryOption, Option } from './add-category-modal.types';
import { AddCategoryModalProps } from './add-category-modal';

type UseAddCategoryModalParams = AddCategoryModalProps;

type UseAddCategoryModalReturn = {
  addingCategory: boolean;
  allowAdd: boolean;
  defaultHeading: string;
  editableHeadingsEnabled: boolean;
  handleChangeCategory: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  handleChangeHeading: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleChangeMobileAppDisplayType: (value: MobileAppProductCollectionDisplayType) => void;
  handleChangeMobileAppFlairType: (value: MobileAppFlair) => void;
  handleChangeSubcategory: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  handleSave: () => void;
  heading: string;
  mobileAppDisplayType: MobileAppProductCollectionDisplayType;
  mobileAppFlairType: MobileAppFlair;
  selectedCategory: CategoryOption | null;
  selectedSubcategory: Option | null;
  visibleCategoryOptions: CategoryOption[] | null;
  visibleSubcategoryOptions: Option[] | null;
};

export function useAddCategoryModal({
  handleAddSection,
  menuSection,
  menuSections,
  onToggle,
  products,
  sectionTypeKey,
}: UseAddCategoryModalParams): UseAddCategoryModalReturn {
  const { UI } = useStores();
  const flags = useFlags();
  const editableHeadingsEnabled = flags[EDITABLE_HOMEPAGE_CAROUSEL_HEADINGS_FLAG] ?? false;
  const [heading, setHeading] = useState(menuSection?.headingOverride ?? '');
  const [selectedCategory, setSelectedCategory] = useState<CategoryOption | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<Option | null>(null);
  const [mobileAppDisplayType, setMobileAppDisplayType] = useState<MobileAppProductCollectionDisplayType>(
    menuSection?.mobileAppDisplayType ?? MobileAppProductCollectionDisplayType.scrollableRow
  );
  const [mobileAppFlairType, setMobileAppFlairType] = useState<MobileAppFlair>(
    menuSection?.mobileAppFlairType ?? MobileAppFlair.none
  );
  const addingCategory = sectionTypeKey === MenuSectionType.category;
  const allowAdd = addingCategory ? !!selectedCategory : !!selectedSubcategory;
  const selectedLabel = selectedSubcategory?.label ?? selectedCategory?.label ?? '';
  const defaultHeading = selectedLabel ? `Popular ${selectedLabel}` : '';

  const visibleCategoryOptions = useMemo(() => getVisibleCategoryOptions(menuSections, sectionTypeKey), [
    menuSections,
    sectionTypeKey,
  ]);

  const visibleSubcategoryOptions = useMemo(() => getVisibleSubcategoryOptions(menuSections, selectedCategory), [
    menuSections,
    selectedCategory,
  ]);

  const handleSave = (): void => {
    const filterMethod = getMenuSectionProductFilter(
      selectedSubcategory ? MenuSectionType.subcategory : MenuSectionType.category
    );

    const foundProducts = filterMethod({
      items: { products, offers: undefined },
      menuSection: { category: selectedCategory?.value ?? '', subcategory: selectedSubcategory?.value ?? '' },
    });

    const productIds = getProductIds(foundProducts as Products);
    const headingOverride = editableHeadingsEnabled ? heading.trim() || undefined : undefined;

    const newMenuSection: LocalSection = {
      id: uuid(),
      dispensaryId: UI.dispensary.id ?? '',
      category: selectedCategory?.value ?? '',
      sectionType: selectedSubcategory ? MenuSectionType.subcategory : MenuSectionType.category,
      subcategory: selectedSubcategory?.value ?? '',
      position: 0,
      label: headingOverride ?? defaultHeading,
      legacyLabel: defaultHeading,
      ...(headingOverride ? { headingOverride } : {}),
      products: productIds,
      count: productIds.length,
      mobileAppDisplayType,
      mobileAppFlairType,
    };

    if (sectionTypeKey) {
      handleAddSection(newMenuSection);
    }

    onToggle();
  };

  const handleChangeHeading = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setHeading(event.target.value);
  };

  const handleChangeMobileAppDisplayType = (value: MobileAppProductCollectionDisplayType): void => {
    setMobileAppDisplayType(value);
  };

  const handleChangeMobileAppFlairType = (value: MobileAppFlair): void => {
    setMobileAppFlairType(value);
  };

  const handleChangeCategory = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const category =
      visibleCategoryOptions?.find((categoryOption) => categoryOption.key === event.target.value) ?? null;
    setSelectedCategory(category);
    setSelectedSubcategory(null);
  };

  const handleChangeSubcategory = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const subcategory =
      visibleSubcategoryOptions?.find((subcategoryOption) => subcategoryOption.key === event.target.value) ?? null;
    setSelectedSubcategory(subcategory);
  };

  return {
    addingCategory,
    allowAdd,
    defaultHeading,
    editableHeadingsEnabled,
    handleChangeCategory,
    handleChangeHeading,
    handleChangeMobileAppDisplayType,
    handleChangeMobileAppFlairType,
    handleChangeSubcategory,
    handleSave,
    heading,
    mobileAppDisplayType,
    mobileAppFlairType,
    selectedCategory,
    selectedSubcategory,
    visibleCategoryOptions,
    visibleSubcategoryOptions,
  };
}
